import { Injectable, NgZone } from '@angular/core';
import * as moment from 'moment';
import Swal from 'sweetalert2';

declare const $: any;
declare const bootstrap: any;

@Injectable({
	providedIn: 'root',
})
export class DatatableService {
	private tableElement: HTMLElement | null = null;
	private datatable: any;

	constructor(private zone: NgZone) {
		// this.setupPaginationRenderer();
	}

	public init(tableId: string = 'kt_data_table'): void {
		setTimeout(() => {
			this.tableElement = document.getElementById(tableId);
			if (!this.tableElement) {
				console.warn(`Element #${tableId} not found`);
				return;
			}

			// Initialize table only once
			if (!$.fn.DataTable.isDataTable(this.tableElement)) {
				this.prepareData();
				this.initializeDatatable();
				this.bindEvents();
				this.handleCheckboxes();
				this.handleStatusFilter();
				this.handleDeleteRows();
			}
		}, 0);
	}

	public destroy(tableId: string): void {
		const tableElement = document.getElementById(tableId);

		if (tableElement && $.fn.DataTable.isDataTable(tableElement)) {
			$(tableElement).DataTable().clear().destroy();
		}
	}

	private debounce<T extends (...args: any[]) => void>(fn: T, wait = 200) {
		let t: any;
		return (...args: Parameters<T>) => {
			clearTimeout(t);
			t = setTimeout(() => fn(...args), wait);
		};
	}


	private prepareData(): any[] {
		const data: any[] = [];

		if (!this.tableElement) return data;

		$(this.tableElement).find('tbody tr').each((_: number, row: HTMLElement) => {
			const rowData: any = {};
			const $row = $(row);
			const tds = $row.find('td');

			if (tds.length === 0 || $row.is(':hidden')) return;

			tds.each((index: number, td: any) => {
				const $td = $(td);
				const label = $td.data('label') || `Column ${index + 1}`;
				const value = $td.text().trim();
				rowData[label] = value;
			});

			data.push(rowData);
		});

		return data;
	}

	private initializeDatatable(): void {
		if (!this.tableElement) return;

		// Reuse the destroy method
		this.destroy(this.tableElement.id);

		// Call the custom pagination renderer before DataTable init
		setTimeout(() => this.upgradePaginationMarkup(), 10);

		// Apply data-order attributes to date column
		setTimeout(() => this.addDateSortingAttribute(), 10);

		// (Do NOT rely on renderer; let DataTables render default buttons)
		this.datatable = $(this.tableElement!).DataTable({
			responsive: true,
			order: [],
			pageLength: 10,
			lengthChange: true,
			searching: true,
			processing: true,
			// scrollX: true,
			// fixedColumns: {
			// 	left: 2
			// },
			columnDefs: [
				{ orderable: false, targets: 0 },
				{ orderable: false, targets: -1 },
			],
			buttons: ['copy', 'excel', 'pdf', 'print'],
			pagingType: 'full_numbers',
			dom: "<'table-responsive'tr>" +
				"<'row'<'col-sm-6 py-4 d-flex align-items-center'i>" +
				"<'col-sm-6 py-4 d-flex justify-content-end'p>>",
			language: {
				search: '',
				searchPlaceholder: 'Search...',
				info: 'Showing _START_ to _END_ of _TOTAL_ records',
				infoEmpty: 'Showing no records',
				lengthMenu: '_MENU_',
				processing: '<span class="spinner-border w-15px h-15px text-muted align-middle me-2"></span><span class="text-gray-600">Loading...</span>',
				paginate: {
					first: '<i class="ki-duotone ki-double-left"><span class="path1"></span><span class="path2"></span></i>',
					last: '<i class="ki-duotone ki-double-right"><span class="path1"></span><span class="path2"></span></i>',
					next: '<i class="ki-duotone ki-right"></i>',
					previous: '<i class="ki-duotone ki-left"></i>',
				},
			},
			search: { caseInsensitive: true, smart: true },
		});

		this.datatable.on('draw', () => {
			this.upgradePaginationMarkup();
			this.handleCheckboxes();
			this.handleDeleteRows();
			this.toggleToolbar();
		});

		// Optional
		// setTimeout(() => $('.dt-search').hide(), 100);

		// Initial bindings
		this.handleCheckboxes();
		this.handleDeleteRows();
		this.toggleToolbar();
	}

	private upgradePaginationMarkup(): void {
		const wrapper = this.tableElement?.closest('.dataTables_wrapper') as HTMLElement | null;
		if (!wrapper) return;

		const paginateDiv = wrapper.querySelector('.dataTables_paginate') as HTMLElement | null;
		if (!paginateDiv) return;

		const api = $(this.tableElement!).DataTable();
		const info = api.page.info();
		const current = info.page;
		const totalPages = info.pages;

		const newNav = document.createElement('nav');
		newNav.setAttribute('aria-label', 'pagination');
		newNav.className = 'dt-paging paging_full';

		const ul = document.createElement('ul');
		ul.className = 'pagination';

		const addButton = (
			label: string | number,
			pageNum: number,
			isActive: boolean = false,
			isDisabled: boolean = false,
			isIcon: boolean = false,
			customClass: string = ''
		) => {
			const li = document.createElement('li');
			li.className = `dt-paging-button page-item ${isDisabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`;

			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = `btn btn-sm btn-primary page-link ${customClass}`;
			btn.setAttribute('role', 'link');
			if (isActive) btn.setAttribute('aria-current', 'page');
			if (isDisabled) {
				btn.setAttribute('aria-disabled', 'true');
				btn.tabIndex = -1;
			}

			btn.innerHTML = isIcon ? `<i class="${label}"></i>` : label.toString();

			btn.addEventListener('click', (e) => {
				e.preventDefault();
				if (!isDisabled) api.page(pageNum).draw('page');
			});

			li.appendChild(btn);
			ul.appendChild(li);
		};

		// First & Previous
		addButton('ki-duotone ki-double-left', 0, false, current === 0, true, 'first');
		addButton('ki-duotone ki-left', current - 1, false, current === 0, true, 'previous');

		// Page numbers
		for (let i = 0; i < totalPages; i++) {
			addButton(i + 1, i, current === i);
		}

		// Next & Last
		addButton('ki-duotone ki-right', current + 1, false, current === totalPages - 1, true, 'next');
		addButton('ki-duotone ki-double-right', totalPages - 1, false, current === totalPages - 1, true, 'last');

		newNav.appendChild(ul);

		// Replace existing pagination
		paginateDiv.innerHTML = '';
		paginateDiv.appendChild(newNav);
	}

	private bindEvents(): void {
		this.handleSearch();
		this.handleFilter();
		this.handleStatusFilter();
		this.handleReset();
	}

	private handleSearch(): void {
		const input = document.querySelector<HTMLInputElement>('[data-table-filter="search"]');
		if (!input || !this.tableElement) return;

		// Always get a *fresh* API reference from the table element
		const api = $(this.tableElement).DataTable();

		// Small debounce
		let t: any;
		const run = (term: string) => {
			clearTimeout(t);
			t = setTimeout(() => {
				// Defensive: only run if API still alive
				if (!api) return;
				// Simple, non-regex, case-insensitive global search
				api.search(term ?? '', false, false).draw();
			}, 200);
		};

		// Bind once
		input.removeEventListener('input', (input as any)._dtSearchHandler as any);
		const handler = (e: Event) => run((e.target as HTMLInputElement).value || '');
		(input as any)._dtSearchHandler = handler;
		input.addEventListener('input', handler);
	}

	private handleFilter(): void {
		const form = document.querySelector('[data-table-filter="form"]');
		if (!form) return;

		const filterButton = form.querySelector('[data-table-filter="filter"]');
		const selects = form.querySelectorAll('select');

		filterButton?.addEventListener('click', () => {
			let search = '';
			selects.forEach((s, i) => {
				const val = (s as HTMLSelectElement).value;
				if (val) search += (i ? ' ' : '') + val;
			});
			this.datatable.search(search).draw();
		});
	}

	private handleStatusFilter(): void {
		const filterStatus = document.querySelector('[data-table-filter="status"]') as HTMLSelectElement;

		if (!filterStatus) return;

		$(filterStatus).on('change', (e: JQuery.ChangeEvent) => {
			let value = (e.target as HTMLSelectElement).value;
			if (value === 'all') value = '';
			const columnIndex = this.getStatusColumnIndex();
			if (columnIndex !== -1) {
				this.datatable.column(columnIndex).search(value).draw();
			}
		});
	}

	private getStatusColumnIndex(): number {
		if (!this.tableElement) return -1;

		const headers = this.tableElement.querySelectorAll('thead th');
		for (let i = 0; i < headers.length; i++) {
			const headerText = headers[i].textContent?.trim().toLowerCase();
			if (headerText === 'status') {
				return i;
			}
		}
		return -1; // Not found
	}

	private handleReset(): void {
		const reset = document.querySelector('[data-table-filter="reset"]');
		if (!reset) return;

		reset.addEventListener('click', () => {
			const form = document.querySelector('[data-table-filter="form"]');
			form?.querySelectorAll('select')?.forEach(select => { $(select).val('').trigger('change'); });

			const input = document.querySelector<HTMLInputElement>('[data-table-filter="search"]');
			if (input) input.value = '';

			this.datatable.search('').draw();
		});
	}


	private handleDeleteRows(): void {
		const buttons = this.tableElement!.querySelectorAll('[data-table-filter="delete_row"]') as NodeListOf<HTMLButtonElement>;

		buttons.forEach((button: HTMLButtonElement) => {
			button.addEventListener('click', (e: MouseEvent) => {
				e.preventDefault();
				const target = e.target as HTMLElement;
				const row = target.closest('tr');

				const name = row?.querySelectorAll('td')[1]?.querySelectorAll('a')[1]?.innerText;
				this.zone.run(() => {
					Swal.fire({
						text: `Are you sure you want to delete ${name}?`,
						icon: 'warning',
						showCancelButton: true,
						confirmButtonText: 'Yes, delete!',
						cancelButtonText: 'No, cancel',
						customClass: {
							confirmButton: 'btn fw-bold btn-danger',
							cancelButton: 'btn fw-bold btn-active-light-primary',
						},
					}).then(result => {
						if (result.isConfirmed && row) {
							this.datatable.row($(row)).remove().draw();
							this.toggleToolbar();
						}
					});
				});
			});
		});
	}

	private handleCheckboxes(): void {
		const checkboxes = this.tableElement!.querySelectorAll<HTMLInputElement>('tbody [type="checkbox"]');
		const headerCheckbox = this.tableElement!.querySelector<HTMLInputElement>('thead [type="checkbox"]');
		const deleteSelected = document.querySelector('[data-table-select="delete_selected"]');

		const toggleToolbar = () => {
			const checked = Array.from(checkboxes).filter(checkbox => checkbox.checked);
			const baseToolbar = document.querySelector('[data-table-toolbar="base"]');
			const selectedToolbar = document.querySelector('[data-table-toolbar="selected"]');
			const count = document.querySelector('[data-table-select="selected_count"]');

			if (checked.length > 0) {
				baseToolbar?.classList.add('d-none');
				selectedToolbar?.classList.remove('d-none');
				if (count) count.innerHTML = checked.length.toString();
			} else {
				baseToolbar?.classList.remove('d-none');
				selectedToolbar?.classList.add('d-none');
			}
		};

		// Handle individual checkbox clicks
		checkboxes.forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				toggleToolbar();

				// Uncheck "select all" if one is unchecked
				if (!checkbox.checked && headerCheckbox) {
					headerCheckbox.checked = false;
				}
			});
		});

		// Handle "Select All" checkbox
		if (headerCheckbox) {
			headerCheckbox.addEventListener('change', () => {
				const isChecked = headerCheckbox.checked;
				checkboxes.forEach(c => c.checked = isChecked);
				toggleToolbar();
			});
		}

		// Handle bulk delete
		deleteSelected?.addEventListener('click', () => {
			this.zone.run(() => {
				Swal.fire({
					text: 'Are you sure you want to delete selected customers?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Yes, delete!',
					cancelButtonText: 'No, cancel',
					customClass: {
						confirmButton: 'btn fw-bold btn-danger',
						cancelButton: 'btn fw-bold btn-active-light-primary',
					},
				}).then(result => {
					if (result.isConfirmed) {
						const selectedRows = this.tableElement!.querySelectorAll('tbody [type="checkbox"]:checked');
						selectedRows.forEach((checkbox: Element) => {
							const row = checkbox.closest('tr') as HTMLTableRowElement;
							if (row) this.datatable.row($(row)).remove().draw();
						});
						if (headerCheckbox) headerCheckbox.checked = false;

						toggleToolbar();
						this.handleCheckboxes(); // rebind after redraw
					}
				});
			});
		});
	}

	private toggleToolbar(): void {
		const allCheckboxes = this.tableElement!.querySelectorAll('tbody [type="checkbox"]');
		const base = document.querySelector('[data-table-toolbar="base"]');
		const selected = document.querySelector('[data-table-toolbar="selected"]');
		const count = document.querySelector('[data-table-select="selected_count"]');

		const checked = Array.from(allCheckboxes).filter((c: any) => c.checked).length;

		if (checked > 0) {
			selected?.classList.remove('d-none');
			base?.classList.add('d-none');
			if (count) count.innerHTML = checked.toString();
		} else {
			selected?.classList.add('d-none');
			base?.classList.remove('d-none');
		}
	}

	private addDateSortingAttribute(): void {
		if (!this.tableElement) return;

		const rows = this.tableElement.querySelectorAll('tbody tr');

		rows.forEach(row => {
			const cells = row.querySelectorAll('td');

			cells.forEach(cell => {
				const text = cell.textContent?.trim();
				if (!text) return;

				// ✅ Try common formats
				const formats = ["DD MMM YYYY, LT", "YYYY-MM-DD", "MM/DD/YYYY", "MMM DD, YYYY", "DD MMM YYYY"];
				let parsed: moment.Moment | null = null;

				for (const format of formats) {
					const tryParse = moment(text, format, true);
					if (tryParse.isValid()) {
						parsed = tryParse;
						break;
					}
				}

				// ✅ Apply sortable format if date is valid
				if (parsed) {
					cell.setAttribute('data-order', parsed.format());
				}
			});
		});
	}

}
